import { useEffect, useState } from "react";
import { findTargetElement, type ShellTargetId } from "./targets";

export interface TargetRect {
  x: number;
  y: number;
  w: number;
  h: number;
}

export type TargetRectStatus = "waiting" | "ready" | "not_found";

export interface TargetRectResult {
  status: TargetRectStatus;
  rect: TargetRect | null;
  element: HTMLElement | null;
}

const READY_TIMEOUT_MS = 1500;

/**
 * Track the bounding rect of an onboarding shell target.
 *
 * Behavior:
 *  - polls via requestAnimationFrame for up to 1500ms looking for the
 *    element. Until then, status === "waiting".
 *  - once found, subscribes via ResizeObserver to the element and
 *    MutationObserver on document.body for layout/DOM churn.
 *  - on element removal, status flips back to "waiting" and the polling
 *    resumes until ready or timeout.
 *
 * No setInterval. No CSS-class selectors. No assumptions about parent
 * structure beyond a `data-onboarding-target` attribute existing in DOM.
 */
export function useTargetRect(
  targetId: ShellTargetId | null,
  active: boolean,
): TargetRectResult {
  const [result, setResult] = useState<TargetRectResult>({
    status: targetId && active ? "waiting" : "ready",
    rect: null,
    element: null,
  });

  useEffect(() => {
    if (!targetId || !active) {
      setResult({ status: "ready", rect: null, element: null });
      return;
    }

    let cancelled = false;
    let rafHandle = 0;
    let resizeObs: ResizeObserver | null = null;
    let mutationObs: MutationObserver | null = null;
    let lateSearchObs: MutationObserver | null = null;
    let trackedEl: HTMLElement | null = null;
    const startedAt = performance.now();

    const measure = (el: HTMLElement) => {
      const r = el.getBoundingClientRect();
      const next: TargetRectResult = {
        status: "ready",
        rect: { x: r.left, y: r.top, w: r.width, h: r.height },
        element: el,
      };
      setResult((prev) => {
        // Skip useless re-renders when the rect hasn't visibly moved.
        if (
          prev.status === "ready" &&
          prev.rect &&
          Math.abs(prev.rect.x - next.rect!.x) < 0.5 &&
          Math.abs(prev.rect.y - next.rect!.y) < 0.5 &&
          Math.abs(prev.rect.w - next.rect!.w) < 0.5 &&
          Math.abs(prev.rect.h - next.rect!.h) < 0.5
        ) {
          return prev;
        }
        return next;
      });
    };

    const attach = (el: HTMLElement) => {
      if (lateSearchObs) {
        lateSearchObs.disconnect();
        lateSearchObs = null;
      }
      trackedEl = el;
      measure(el);
      try {
        resizeObs = new ResizeObserver(() => {
          if (trackedEl) measure(trackedEl);
        });
        resizeObs.observe(el);
      } catch {
        // ResizeObserver missing — fall back to mutation observer alone.
      }
      try {
        mutationObs = new MutationObserver(() => {
          // If our element is no longer attached, restart the search.
          if (trackedEl && !document.contains(trackedEl)) {
            detach();
            if (!cancelled) {
              setResult({
                status: "waiting",
                rect: null,
                element: null,
              });
              poll();
            }
            return;
          }
          if (trackedEl) measure(trackedEl);
        });
        mutationObs.observe(document.body, {
          childList: true,
          subtree: true,
          attributes: true,
          attributeFilter: ["data-onboarding-target", "class", "style"],
        });
      } catch {
        // ignore
      }
    };

    const detach = () => {
      if (resizeObs) {
        resizeObs.disconnect();
        resizeObs = null;
      }
      if (mutationObs) {
        mutationObs.disconnect();
        mutationObs = null;
      }
      if (lateSearchObs) {
        lateSearchObs.disconnect();
        lateSearchObs = null;
      }
      trackedEl = null;
    };

    const watchForLateTarget = () => {
      if (cancelled || lateSearchObs) return;

      const immediate = findTargetElement(targetId);
      if (immediate) {
        attach(immediate);
        return;
      }

      try {
        lateSearchObs = new MutationObserver(() => {
          const lateEl = findTargetElement(targetId);
          if (!lateEl) return;
          attach(lateEl);
        });
        lateSearchObs.observe(document.body, {
          childList: true,
          subtree: true,
          attributes: true,
          attributeFilter: [
            "data-onboarding-target",
            "class",
            "style",
            "hidden",
          ],
        });
      } catch {
        // ignore
      }
    };

    const poll = () => {
      if (cancelled) return;
      const el = findTargetElement(targetId);
      if (el) {
        attach(el);
        return;
      }
      if (performance.now() - startedAt > READY_TIMEOUT_MS) {
        setResult({ status: "not_found", rect: null, element: null });
        watchForLateTarget();
        return;
      }
      rafHandle = requestAnimationFrame(poll);
    };

    poll();

    const onResize = () => {
      if (trackedEl) measure(trackedEl);
    };
    const onScroll = () => {
      if (trackedEl) measure(trackedEl);
    };
    window.addEventListener("resize", onResize, { passive: true });
    window.addEventListener("scroll", onScroll, {
      passive: true,
      capture: true,
    });

    return () => {
      cancelled = true;
      if (rafHandle) cancelAnimationFrame(rafHandle);
      detach();
      window.removeEventListener("resize", onResize);
      window.removeEventListener("scroll", onScroll, { capture: true });
    };
  }, [targetId, active]);

  return result;
}
